import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import {
  Box,
  Typography,
  Container,
  Card,
  CardContent,
  CircularProgress,
  Grid
} from '@mui/material';
import { Assignment as AssignmentIcon } from '@mui/icons-material';
import { motion } from 'framer-motion';

const cardVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.5 } },
  hover: { scale: 1.03, transition: { duration: 0.2 } }
};

const TeacherAssignmentsScreen = () => {
  const navigate = useNavigate();
  const { state } = useLocation();
  const { teacherId, schoolId } = state || {};
  const [assignments, setAssignments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    const fetchAssignments = async () => {
      try {
        if (!teacherId || !schoolId) {
          throw new Error('Missing teacher or school ID');
        }
        const response = await axios.get(
          `https://kidsm.vercel.app/api/schools/teachers/${teacherId}/assignments`,
          { params: { schoolId } }
        );
        setAssignments(response.data || []);
      } catch (error) {
        console.error('Error fetching assignments:', error);
        setError(error.message || 'Failed to load assignments');
      } finally {
        setLoading(false);
      }
    };

    fetchAssignments();
  }, [teacherId, schoolId]);

  if (loading) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          bgcolor: '#f5f5f5'
        }}
      >
        <CircularProgress color="primary" />
        <Typography sx={{ mt: 2, color: '#6200ee' }}>
          Loading assignments...
        </Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          bgcolor: '#f5f5f5'
        }}
      >
        <Typography color="error">{error}</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#f5f5f5', pt: 8 }}>
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <motion.div variants={cardVariants} initial="hidden" animate="visible">
          <Typography
            variant="h4"
            sx={{ fontWeight: 'bold', color: '#333', mb: 4 }}
          >
            Teacher Assignments
          </Typography>
          {assignments.length === 0 ? (
            <Card sx={{ p: 3, textAlign: 'center', borderRadius: '10px', boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
              <CardContent>
                <AssignmentIcon sx={{ fontSize: 50, color: '#ccc' }} />
                <Typography sx={{ fontSize: '18px', color: '#666', mt: 2 }}>
                  No assignments created yet
                </Typography>
              </CardContent>
            </Card>
          ) : (
            <Grid container spacing={3}>
              {assignments.map((item) => (
                <Grid item xs={12} sm={6} md={4} key={item._id}>
                  <motion.div
                    variants={cardVariants}
                    initial="hidden"
                    animate="visible"
                    whileHover="hover"
                  >
                    <Card
                      sx={{
                        borderRadius: '10px',
                        boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                        cursor: 'pointer',
                        borderLeft: '4px solid #6200ee'
                      }}
                      onClick={() =>
                        navigate('/assignment-responses', {
                          state: { assignmentId: item._id, schoolId }
                        })
                      }
                    >
                      <CardContent>
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                          <AssignmentIcon sx={{ color: '#6200ee', mr: 2 }} />
                          <Box>
                            <Typography
                              sx={{ fontSize: '18px', fontWeight: 'bold', color: '#333' }}
                            >
                              {item.title}
                            </Typography>
                            <Typography sx={{ fontSize: '14px', color: '#6200ee' }}>
                              {item.subject}
                            </Typography>
                          </Box>
                        </Box>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 2 }}>
                          <Typography sx={{ fontSize: '14px', color: '#666' }}>
                            Due: {new Date(item.dueDate).toLocaleDateString()}
                          </Typography>
                          <Typography sx={{ fontSize: '14px', color: '#666', fontStyle: 'italic' }}>
                            {item.responses.length}{' '}
                            {item.responses.length === 1 ? 'response' : 'responses'}
                          </Typography>
                        </Box>
                      </CardContent>
                    </Card>
                  </motion.div>
                </Grid>
              ))}
            </Grid>
          )}
        </motion.div>
      </Container>
    </Box>
  );
};

export default TeacherAssignmentsScreen;